import axiosInstance from "../../utils/axiosConfig";
import React, { useEffect, useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import CopyToClipboard from "react-copy-to-clipboard";
import { formatAmount, truncateWord } from "../../utils/FormatPerson";
import { resolveBankNameFromLists } from "../../lib/api/bankNameResolver";

type Props = {
  currency: string;
  currencyUSD: number;
  currencyNGN: number;
  status: number;
  network: string;
  date: string;
  tsxnId: string;
  userId: string;
  setOrderModal: any;
  payoutStatus: any;
  setRefetchCount: any;
  setCryptoConfirmationModal: any;
  cryptoRate: any | number;
  orderss: any;
};

interface Bank {
  accountNumber: string;
  bankCode: string;
  accountName: string;
  isDefault: boolean;
  _id: string;
}

const OrderConfrimation = ({
  currency,
  currencyUSD,
  currencyNGN,
  status,
  network,
  userId,
  setOrderModal,
  payoutStatus,
  setCryptoConfirmationModal,
  cryptoRate,
  orderss,
}: Props) => {
  const { isAuthenticated } = useAuth();
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [banks, setBanks] = useState<Bank | null>(null);
  const [allBanks, setAllBanks] = useState<any[]>([]);
  const [allOldBanks, setAllOldBanks] = useState<any[]>([]);

  // Copy states
  const [copyMail, setCopyMail] = useState<boolean>(false);
  const [copyAccount, setCopyAccount] = useState<boolean>(false);
  const [copyAmount, setCopyAmount] = useState<boolean>(false);
  const [copyQuote, setCopyQuote] = useState<boolean>(false);
  const [copyId, setCopyId] = useState<boolean>(false);
  const [copyFromDeexUser, setCopyFromDeexUser] = useState<boolean>(false);
  const [copyToDeexUser, setCopyToDeexUser] = useState<boolean>(false);

  // Check if transaction is confirmed
  useEffect(() => {
    const isConfirmed =
      orderss.status === "COMPLETED" ||
      orderss.transactionType === "crypto-deposit" ||
      orderss.transactionType === "deex-crypto-withdrawals" ||
      orderss.transactionType === "deex-deposits" ||
      orderss.transactionType === "payouts" ||
      orderss.transactionType === "deex-asset-swaps";

    setConfirmed(isConfirmed);
  }, [orderss.status, orderss.transactionType]);

  // Fetch user data
  useEffect(() => {
    axiosInstance
      .get(`${API_URL}api/users/${userId}`)
      .then((r: any) => {
        const userData = r.data.data.user;
        setEmail(userData.email.address);
        setFirstName(userData.profile.firstName);
        setLastName(userData.profile.lastName);

        const bankList: Bank[] = userData?.banks || [];
        const defaultBank =
          bankList.find((bank: Bank) => bank.isDefault) || null;
        setBanks(defaultBank);
      })
      .catch((e: any) => {
        console.log(e.response.data.message);
      });
  }, [isAuthenticated, userId]);

  // Fetch bank list
  useEffect(() => {
    Promise.all([
      axiosInstance.get(`${API_URL}api/banks/palmpay`),
      axiosInstance.get(`${API_URL}api/banks`),
    ])
      .then(([palmpayRes, oldRes]: any[]) => {
        setAllBanks(
          Array.isArray(palmpayRes?.data?.data) ? palmpayRes.data.data : []
        );
        setAllOldBanks(
          Array.isArray(oldRes?.data?.data) ? oldRes.data.data : []
        );
      })
      .catch((e: any) => {
        console.log(e?.response?.data?.message);
      });
  }, []);

  // Helper functions
  const getTransactionTypeDisplay = (type: string) => {
    switch (type) {
      case "deex-deposits":
        return "Deposit";
      case "deex-crypto-withdrawals":
        return "Withdrawal";
      case "deex-asset-swaps":
        return "Asset Swap";
      case "gift-card-transactions":
        return "Gift Card";
      case "payouts":
        return "Payout";
      case "crypto-deposit":
        return "Crypto Deposit";
      default:
        return type;
    }
  };

  const formatDate = () => {
    const dateString = orderss.createdAt || orderss.processedAt;
    const date = new Date(dateString);

    const day = date.getDate();
    const ordinalSuffix = (day: number) => {
      switch (day) {
        case 1:
        case 21:
        case 31:
          return "st";
        case 2:
        case 22:
          return "nd";
        case 3:
        case 23:
          return "rd";
        default:
          return "th";
      }
    };

    return `${date.toLocaleString("default", {
      month: "short",
    })} ${day}${ordinalSuffix(
      day
    )}, ${date.getFullYear()} | ${date.toLocaleString("default", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })}`;
  };

  const formatAmountDisplay = (transaction: any) => {
    if (
      transaction.transactionType === "deex-deposits" ||
      transaction.transactionType === "deex-crypto-withdrawals" ||
      transaction.transactionType === "crypto-deposit"
    ) {
      return `${formatAmount(
        transaction.amount
      )} ${transaction.currency.toUpperCase()}`;
    } else if (transaction.transactionType === "deex-asset-swaps") {
      return `${formatAmount(transaction.from.amount)}`;
    } else if (transaction.transactionType === "payouts") {
      if (transaction.transaction?.type === "DEEX-POINT") {
        return `₦ ${formatAmount(transaction.asset.amount)}`;
      } else {
        return `${formatAmount(
          transaction.asset.amount
        )} ${transaction.asset.name.toUpperCase()}`;
      }
    } else if (transaction.transactionType === "gift-card-transactions") {
      const totalAmount = transaction.tradeValue.reduce(
        (total: number, item: any) => total + item.amount,
        0
      );
      return `$ ${formatAmount(totalAmount)}`;
    } else {
      return `₦ ${formatAmount(transaction.asset.amount)}`;
    }
  };

  // Handle copy to clipboard with timeout
  const handleCopy = (
    setCopyState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setCopyState(true);
    setTimeout(() => {
      setCopyState(false);
    }, 3000);
  };

  // Get recipient address for crypto withdrawals
  const getRecipientAddress = () => {
    if (orderss.transactionType === "deex-crypto-withdrawals") {
      return truncateWord(orderss?.toAddress || orderss?.toDeexUser) ?? "---";
    }
    return email;
  };

  return (
    <div className="fixed inset-0 flex font-manrope justify-center items-start overflow-auto pt-16 pb-16  bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="w-11/12 mds:10/12 lgss:w-2/5 transition-all p-6 ease-in duration-1000  rounded-xl bg-[#0D0D0D] ">
        <div className="flex  pt-4 justify-between items-center">
          <h4 className="font-bold text-[17px] font-sora text-white">
            {getTransactionTypeDisplay(orderss?.transactionType)} Details
          </h4>
          <FaTimesCircle
            onClick={() => setOrderModal((prev: any) => !prev)}
            className="text-white text-[26px] cursor-pointer"
          />
        </div>
        <div className="w-full flex-col mt-12 justify-center   items-center ">
          <h4 className="text-[42px] text-[#DEE1E6] text-center font-bold font-sora ">
            {formatAmountDisplay(orderss)}
          </h4>
          {orderss?.transactionType === "deex-asset-swaps" && (
            <h4 className="text-[18px] capitalize text-[#DEE1E6] text-center font-bold font-sora ">
              {orderss?.from?.asset} → {orderss?.to?.asset}
            </h4>
          )}
          <h4 className="text-[24px] capitalize mt-2 text-[#DEE1E6] text-center font-bold font-sora ">
            {firstName} {lastName}
          </h4>
        </div>

        <div className="w-full h-[1px] bg-[#AEBCC6] mt-12 mb-4" />

        {/* Digital Asset Section */}
        {orderss?.transactionType !== "deex-asset-swaps" &&
          orderss?.transactionType !== "gift-card-transactions" && (
            <div className="w-full mt-2 flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Digital Asset
              </h4>
              <h4 className="text-[#869AA9] text-[15px] uppercase">
                {orderss.transactionType === "payouts"
                  ? orderss?.asset?.name
                  : orderss.transaction?.type === "DEEX-POINT"
                  ? "DEEX-POINT"
                  : orderss?.transactionType === "deex-deposits"
                  ? orderss?.currency?.toUpperCase()
                  : currency}
              </h4>
            </div>
          )}

        {/* Transaction ID Section */}
        <div className="w-full flex mt-2 justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Transaction ID
          </h4>
          <CopyToClipboard
            text={orderss?._id || ""}
            onCopy={() => handleCopy(setCopyId)}
          >
            {copyId ? (
              <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                Copied
              </h4>
            ) : (
              <h4 className="text-secondary_button underline text-[13px] cursor-pointer break-all">
                {orderss?._id}
              </h4>
            )}
          </CopyToClipboard>
        </div>

        {/* Network and Rate Section */}
        {orderss.transaction?.type !== "DEEX-POINT" &&
          orderss?.transactionType !== "deex-asset-swaps" &&
          orderss?.transactionType !== "gift-card-transactions" &&
          orderss?.transactionType !== "deex-deposits" && (
            <>
              <div className="w-full mt-2 flex justify-between items-center">
                <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                  Network
                </h4>
                <h4 className="text-[#869AA9] text-[15px] uppercase">
                  {orderss.transactionType === "payouts"
                    ? "---"
                    : network ?? "---"}
                </h4>
              </div>
              {orderss.transactionType !== "deex-crypto-withdrawals" && (
                <div className="w-full mt-2 flex justify-between items-center">
                  <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                    Rate
                  </h4>
                  <h4 className="text-[#869AA9] text-[15px] ">
                    {orderss?.transactionType === "payouts"
                      ? `${formatAmount(
                          orderss?.payoutAmount / orderss?.asset?.amountInUSD
                        )} /$`
                      : cryptoRate}
                  </h4>
                </div>
              )}
            </>
          )}

        {/* Recipient Email Section */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Recipient Email
          </h4>
          <CopyToClipboard text={email} onCopy={() => handleCopy(setCopyMail)}>
            {copyMail ? (
              <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                Copied
              </h4>
            ) : (
              <h4 className="text-[#0C84DA] text-[14px] cursor-pointer">
                {email}
              </h4>
            )}
          </CopyToClipboard>
        </div>

        {/* From Deex User ID Section */}
        {orderss?.transactionType === "deex-deposits" &&
          orderss?.fromDeexUser && (
            <div className="w-full mt-2 flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                From Deex User ID
              </h4>
              <CopyToClipboard
                text={orderss?.fromDeexUser}
                onCopy={() => handleCopy(setCopyFromDeexUser)}
              >
                {copyFromDeexUser ? (
                  <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                    Copied
                  </h4>
                ) : (
                  <h4 className="text-[#0C84DA] text-[14px] cursor-pointer">
                    {orderss?.fromDeexUser}
                  </h4>
                )}
              </CopyToClipboard>
            </div>
          )}

        {/* To Deex User ID Section */}
        {orderss?.transactionType === "deex-crypto-withdrawals" &&
          orderss?.toDeexUser && (
            <div className="w-full mt-2 flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                To Deex User ID
              </h4>
              <CopyToClipboard
                text={orderss?.toDeexUser}
                onCopy={() => handleCopy(setCopyToDeexUser)}
              >
                {copyToDeexUser ? (
                  <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                    Copied
                  </h4>
                ) : (
                  <h4 className="text-[#0C84DA] text-[14px] cursor-pointer">
                    {orderss?.toDeexUser}
                  </h4>
                )}
              </CopyToClipboard>
            </div>
          )}

        {/* Payouts Section */}
        {orderss?.transactionType === "payouts" && (
          <>
            <div className="w-full mt-2 flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Amount to pay
              </h4>
              <CopyToClipboard
                text={
                  orderss.transactionType === "payouts"
                    ? orderss?.payoutAmount?.toString()
                    : currencyNGN?.toString()
                }
                onCopy={() => handleCopy(setCopyAmount)}
              >
                {copyAmount ? (
                  <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                    Copied
                  </h4>
                ) : (
                  <h4 className="text-[#869AA9] text-[16px] cursor-pointer">
                    {orderss.transactionType === "payouts"
                      ? `₦${formatAmount(orderss?.payoutAmount)}`
                      : `₦${formatAmount(currencyNGN)}`}
                  </h4>
                )}
              </CopyToClipboard>
            </div>
            <div className="w-full mt-2 flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Cash destination
              </h4>
              <CopyToClipboard
                text={banks ? banks?.accountNumber : ""}
                onCopy={() => handleCopy(setCopyAccount)}
              >
                {copyAccount ? (
                  <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                    Copied
                  </h4>
                ) : (
                  <div className="text-[#0C84DA] cursor-pointer flex gap-2 justify-center items-center font-bold text-[13px] ">
                    {banks && banks.accountNumber}{" "}
                    <h5 className="text-[#869AA9]">
                      {resolveBankNameFromLists(
                        banks?.bankCode,
                        allBanks,
                        allOldBanks
                      )}
                    </h5>
                  </div>
                )}
              </CopyToClipboard>
            </div>
          </>
        )}

        {/* Points Amount Section */}
        {orderss?.transactionType === "payouts" &&
          orderss?.transaction?.type === "DEEX-POINT" && (
            <div className="w-full flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Points Amount
              </h4>
              <h4 className="text-[#869AA9] text-[14px]">
                ₦{formatAmount(orderss?.asset?.amount)}
              </h4>
            </div>
          )}

        {/* Fee Section */}
        {orderss?.fees && (
          <div className="w-full flex justify-between mt-2 items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Fee</h4>
            <h4 className="text-[#869AA9] text-[14px]">
              {formatAmount(orderss?.fees)} {orderss?.currency?.toUpperCase()}
            </h4>
          </div>
        )}

        {/* Status Section */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Status</h4>
          {orderss.transactionType === "crypto-deposit" ||
          orderss.transactionType === "deex-deposits" ? (
            <td className="text-white text-[15px] w-3/12 whitespace-nowrap flex justify-end  overflow-hidden">
              <h4 className="text-[#03C668] flex justify-center items-center w-full bg-[#013019] tracking-wider h-[26px] rounded-full text-[11px] font-bold">
                COMPLETED
              </h4>
            </td>
          ) : (
            <div className="text-white text-[15px] w-3/12 whitespace-nowrap flex justify-end  overflow-hidden">
              <h4
                className={
                  orderss?.status.toUpperCase() === "PENDING"
                    ? "text-[#CB9200] flex justify-center items-center w-full bg-[#181818] tracking-wider h-[26px] rounded-full text-[11px] font-bold"
                    : orderss?.status.toUpperCase() === "FAILED"
                    ? "text-red-500  flex justify-center items-center w-full bg-[#181818] tracking-wider h-[26px] rounded-full text-[11px] font-bold"
                    : orderss?.status.toUpperCase() === "COMPLETED" ||
                      orderss?.status.toUpperCase() === "SUCCESSFUL"
                    ? "text-[#03C668] flex justify-center items-center w-full bg-[#0E2B1F] tracking-wider h-[26px] rounded-full text-[11px] font-bold"
                    : ""
                }
              >
                {orderss?.status}
              </h4>
            </div>
          )}
        </div>

        {/* Date Section */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">Date</h4>
          <h4 className="text-[#869AA9] text-[14px] ">{formatDate()}</h4>
        </div>

        {/* Provider Section */}
        {orderss?.provider ? (
          <div className="w-full mt-2 flex justify-between items-center">
            <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
              Provider
            </h4>
            <h4 className="text-[#869AA9] text-[14px] ">
              {orderss?.provider?.name?.toUpperCase()}
            </h4>
          </div>
        ) : null}

        {/* Transaction Type Section */}
        <div className="w-full mt-2 flex justify-between items-center">
          <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
            Transaction Type
          </h4>
          <h4 className="text-[#869AA9] text-[14px] ">
            {getTransactionTypeDisplay(orderss?.transactionType)}{" "}
            {orderss?.transactionType === "deex-crypto-withdrawals" &&
              orderss?.utility && (
                <span className="text-[#869AA9] text-[14px] cursor-pointer">
                  ({orderss?.utility?.type})
                </span>
              )}
            {orderss?.transactionType === "deex-deposits" &&
              orderss?.fromDeexUser && (
                <span className="text-[#869AA9] text-[14px] cursor-pointer">
                  From Deex User ID
                </span>
              )}
            {orderss?.transactionType === "deex-crypto-withdrawals" &&
              orderss?.toDeexUser && (
                <span className="text-[#869AA9] text-[14px] cursor-pointer">
                  To Deex User
                </span>
              )}
          </h4>
        </div>

        {/* Asset Swap Details */}
        {orderss?.transactionType === "deex-asset-swaps" && (
          <>
            <div className="w-full flex mt-2 justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                From Asset
              </h4>
              <h4 className="text-[#869AA9] text-[14px] uppercase">
                {orderss?.from?.asset}
              </h4>
            </div>
            <div className="w-full flex mt-2 justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                To Asset
              </h4>
              <h4 className="text-[#869AA9] text-[14px] uppercase">
                {orderss?.to?.asset}
              </h4>
            </div>
            <div className="w-full flex mt-2 justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Quote ID
              </h4>
              <CopyToClipboard
                text={orderss?.quoteID}
                onCopy={() => handleCopy(setCopyQuote)}
              >
                {copyQuote ? (
                  <h4 className="text-[12px] xs:text-[14px] text-[#869AA9]">
                    Copied
                  </h4>
                ) : (
                  <h4 className="text-[#869AA9] text-[14px] uppercase">
                    {truncateWord(orderss?.quoteID ?? "---")}
                  </h4>
                )}
              </CopyToClipboard>
            </div>
          </>
        )}

        {/* Gift Card Details */}
        {orderss?.transactionType === "gift-card-transactions" && (
          <>
            <div className="w-full flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Card Type
              </h4>
              <h4 className="text-[#869AA9] text-[14px] uppercase">
                {orderss?.card?.name}
              </h4>
            </div>
            <div className="w-full flex justify-between items-center">
              <h4 className="text-[#AEBCC6] text-[14px] font-semibold">
                Card Value
              </h4>
              <h4 className="text-[#869AA9] text-[14px]">
                $
                {formatAmount(
                  orderss?.tradeValue?.reduce(
                    (total: number, item: any) => total + item.amount,
                    0
                  )
                )}
              </h4>
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col w-full  mt-6">
          {!confirmed && (
            <button
              onClick={() => {
                if (status === 2) {
                  setCryptoConfirmationModal((prev: any) => !prev);
                  setOrderModal((prev: any) => !prev);
                }
              }}
              disabled={confirmed}
              className={
                confirmed
                  ? "flex justify-center items-center h-[47px] rounded-lg bg-[#1E1E1E] text-white font-medium "
                  : "flex justify-center items-center h-[47px] rounded-lg bg-[#0B84CB] text-white font-medium "
              }
            >
              <div>
                <h4>
                  {status === 1
                    ? "PENDING"
                    : status === 2
                    ? "Create Payout"
                    : ""}
                </h4>
              </div>
            </button>
          )}
          <a
            href={`mailto:${email}`}
            className="flex justify-center items-center mt-8 h-[47px] rounded-lg border bg-transparent border-[#0B84CB] text-white font-medium "
          >
            Contact the client
          </a>
        </div>
      </div>
    </div>
  );
};

export default OrderConfrimation;
